import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Dialog, InputAdornment, List, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    TrackedIconButton as IconButton,
    TrackedListItemButton as ListItemButton,
} from '../../components/TrackedComponents';
import useApi from '../../hooks/api';

interface SwitchFleetMenuProps {
    fleets: Record<string, string>;
    onClose: CallableFunction;
    fullScreen?: boolean;
}

const SwitchFleetMenu: React.FC<SwitchFleetMenuProps> = (props: SwitchFleetMenuProps) => {
    const { t } = useTranslation();
    const { setRefreshToken } = useApi();
    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        searchInputRef.current?.focus();
    }, []);

    const filteredFleets = useMemo(() => {
        const entries = Object.entries(props.fleets).sort((a, b) => a[0].localeCompare(b[0]));
        const q = searchQuery.trim().toLowerCase();
        if (!q) {
            return entries;
        }
        return entries.filter(([fleetName]) => fleetName.toLowerCase().includes(q));
    }, [props.fleets, searchQuery]);

    return (
        <Dialog
            open={true}
            onClose={() => props.onClose()}
            fullWidth
            fullScreen={props.fullScreen}
            sx={{
                '& .MuiDialog-container': {
                    '& .MuiPaper-root': {
                        width: '100%',
                        maxWidth: props.fullScreen ? undefined : '350px',
                        ...(!props.fullScreen && {
                            height: '60vh',
                            maxHeight: '60vh',
                            display: 'flex',
                            flexDirection: 'column',
                        }),
                    },
                },
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        backgroundColor: 'secondary.main',
                        flexShrink: 0,
                        p: 0.5,
                        pl: 1.5,
                        zIndex: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography>{t('navigator.switch_fleet')}</Typography>
                    <IconButton id="btn-close-modal" size="small" onClick={() => props.onClose()}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box sx={{ px: 1.5, pt: 1, pb: 0.5, flexShrink: 0 }}>
                    <TextField
                        inputRef={searchInputRef}
                        id="switch-fleet-search"
                        data-testid="switch-fleet-search"
                        fullWidth
                        size="small"
                        placeholder={t('navigator.switch_fleet_search_placeholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                    <List disablePadding>
                        {filteredFleets.length === 0 && searchQuery.trim() ? (
                            <Box sx={{ px: 2, py: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('navigator.switch_fleet_no_results')}
                                </Typography>
                            </Box>
                        ) : filteredFleets.length > 0 ? (
                            filteredFleets.map(([fleetName, refreshToken]) => (
                                <ListItemButton
                                    id={`${fleetName.toLocaleLowerCase()}-fleet`}
                                    data-testid={`${fleetName.toLocaleLowerCase()}-fleet`}
                                    key={fleetName}
                                    onClick={() => {
                                        setRefreshToken(refreshToken);
                                        window.location.reload();
                                    }}
                                >
                                    <Typography>{fleetName}</Typography>
                                </ListItemButton>
                            ))
                        ) : null}
                    </List>
                </Box>
            </Box>
        </Dialog>
    );
};

export default SwitchFleetMenu;
